import { AnimationClip, Bone, PropertyBinding, QuaternionKeyframeTrack, VectorKeyframeTrack } from 'three';
import { normalizeBoneName } from './boneNames.js';
import { BONES } from '../../scripts/rig-spec.mjs';

/**
 * The animation library, packed (step 95).
 *
 * The game used to download sixteen Mixamo FBX files — 8 MB, uncompressed by
 * the CDN, a binary format the browser then parsed on the main thread — to
 * read, out of each, a fraction of what was in it. The posed retarget
 * (`retarget.js`) asks a source rig for exactly four things: each bone's name,
 * parent and rest rotation; the root's rest height (for scale); a rotation
 * track per bone; and the root's position track. And it only asks about the
 * bones this game's skeleton has — twenty, out of X Bot's sixty-five, every
 * one of whose ancestors is also among the twenty.
 *
 * So a clip is packed as that and no more: the twenty bones' rest rotations,
 * every rotation track **resampled at the clip's own key times** — which are
 * the times the retarget evaluates it at, so it sees the same values — and the
 * root's positions, rounded to what the eye cannot see past. One JSON file for
 * the lot, which the CDN compresses. `unpackClip` hands the retarget a
 * hierarchy and a clip it reads exactly as it read the FBX's, and
 * `clipPack.test.js` holds the two retargets to within a hundredth of a degree.
 *
 * `scripts/bake-clips.mjs` writes it; nothing else needs the FBX at runtime.
 */

/** The bones this game's skeleton has, as normalised names. */
const KEEP = new Set(BONES.map((bone) => normalizeBoneName(bone.name)));

/** Decimal places kept: rotations (unit quaternions), root position (source cm), times (s). */
export const PRECISION = Object.freeze({ rotation: 3, position: 2, time: 4 });

const round = (places) => {
  const k = 10 ** places;
  return (value) => Math.round(value * k) / k;
};

/** Every Bone under an object, parents before children. */
function collectBones(root) {
  const bones = [];
  root?.traverse?.((child) => {
    if (child.isBone) bones.push(child);
  });
  return bones;
}

/**
 * One clip off a loaded FBX group, packed. `root` is its first bone (what
 * `findRootBone` returns), `clip` its animation.
 */
export function packClip({ name, clip, root }) {
  const all = collectBones(root);
  const kept = all.filter((bone) => KEEP.has(normalizeBoneName(bone.name)));
  const index = new Map(kept.map((bone, i) => [bone, i]));
  // Every kept bone's parent must be kept too, or the hierarchy the retarget
  // walks would skip a rotation. True of X Bot; checked rather than assumed.
  for (const bone of kept) {
    if (bone !== root && !index.has(bone.parent)) {
      throw new Error(`${name}: "${bone.name}" keeps a parent ("${bone.parent?.name}") the pack drops`);
    }
  }

  const times = [...new Set(clip.tracks.flatMap((track) => [...track.times]))].sort((a, b) => a - b);
  const byBone = new Map();
  let rootTrack = null;
  for (const track of clip.tracks) {
    const parsed = PropertyBinding.parseTrackName(track.name);
    const key = normalizeBoneName(parsed.nodeName);
    if (parsed.propertyName === 'quaternion') byBone.set(key, track);
    if (parsed.propertyName === 'position' && key === normalizeBoneName(root.name)) rootTrack = track;
  }

  const r = round(PRECISION.rotation);
  const p = round(PRECISION.position);
  const rotations = kept.map((bone) => {
    const track = byBone.get(normalizeBoneName(bone.name));
    if (!track) return null;
    const interpolant = track.createInterpolant();
    return times.flatMap((time) => [...interpolant.evaluate(time)].map(r));
  });
  const position = rootTrack
    ? (() => {
        const interpolant = rootTrack.createInterpolant();
        return times.flatMap((time) => [...interpolant.evaluate(time)].map(p));
      })()
    : null;

  return {
    name,
    duration: round(PRECISION.time)(clip.duration),
    times: times.map(round(PRECISION.time)),
    rootY: p(root.position.y),
    bones: kept.map((bone) => ({
      name: bone.name,
      parent: bone === root ? -1 : index.get(bone.parent),
      rest: bone.quaternion.toArray().map(r),
    })),
    rotations,
    position,
  };
}

/** A flat run of quaternions, each scaled back to length one. */
function unit(values) {
  const out = new Float32Array(values.length);
  for (let k = 0; k < values.length; k += 4) {
    const length = Math.hypot(values[k], values[k + 1], values[k + 2], values[k + 3]) || 1;
    for (let j = 0; j < 4; j += 1) out[k + j] = values[k + j] / length;
  }
  return out;
}

/**
 * A packed clip, as the retarget wants it: `{ name, clip, sourceRoot }` — a
 * bone hierarchy carrying the rest rotations and the root's height, and a clip
 * with a rotation track per bone and the root's position track.
 */
export function unpackClip(entry) {
  const bones = entry.bones.map((spec) => {
    const bone = new Bone();
    bone.name = spec.name;
    // Normalised on the way back in: four decimal places leave a quaternion's
    // length off by about 1e-4, which is no rotation at all and is still not
    // a unit quaternion — and everything downstream assumes one.
    bone.quaternion.fromArray(spec.rest).normalize();
    return bone;
  });
  entry.bones.forEach((spec, i) => {
    if (spec.parent >= 0) bones[spec.parent].add(bones[i]);
  });
  const sourceRoot = bones[entry.bones.findIndex((spec) => spec.parent === -1)];
  sourceRoot.position.set(0, entry.rootY, 0);

  const tracks = [];
  entry.bones.forEach((spec, i) => {
    const values = entry.rotations[i];
    if (values) tracks.push(new QuaternionKeyframeTrack(`${spec.name}.quaternion`, entry.times, unit(values)));
  });
  if (entry.position) tracks.push(new VectorKeyframeTrack(`${sourceRoot.name}.position`, entry.times, entry.position));
  return { name: entry.name, clip: new AnimationClip(entry.name, entry.duration, tracks), sourceRoot };
}

/**
 * A packed library, fetched and unpacked **once** per URL however many bodies
 * are retargeted from it: `[{ name, clip, sourceRoot }]`. A failure is not
 * remembered, so a retry goes back to the network.
 */
const packs = new Map();
export function loadClipPack(url) {
  if (!packs.has(url)) {
    const pending = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`failed to load ${url}: ${response.status}`);
        return response.json();
      })
      .then((data) => (data?.clips ?? []).map(unpackClip));
    pending.catch(() => packs.delete(url));
    packs.set(url, pending);
  }
  return packs.get(url);
}
